import { useMemo, useState } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import PageHeader from '@/components/feature/PageHeader';
import Chip from '@/components/base/Chip';
import EmptyState from '@/components/base/EmptyState';
import ContentListItem from '@/components/base/ContentListItem';
import { getByCategory, getTags, sortByHot, sortByLatest, type ContentItem } from '@/utils/content';

const dateRanges = [
  { key: 'all', label: '全部时间', days: 9999 },
  { key: '7', label: '近 7 天', days: 7 },
  { key: '30', label: '近 30 天', days: 30 },
];

const PAGE_SIZE = 6;

export default function News() {
  useDocumentTitle(
    '小 - 新闻资讯频道 - 多元内容聚合平台',
    '汇集社会、科技、财经等领域的新闻资讯与热点解读，帮助您快速掌握最新动态。',
    '小,新闻资讯,热点新闻,社会,科技,财经'
  );

  const allNews = useMemo(() => getByCategory('news'), []);
  const tags = useMemo(() => getTags('news'), []);

  const newest = useMemo(
    () => allNews.reduce((acc, item) => (item.date > acc ? item.date : acc), '2000-01-01'),
    [allNews]
  );

  const [tag, setTag] = useState('全部');
  const [range, setRange] = useState('all');
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const rangeCfg = dateRanges.find((r) => r.key === range) ?? dateRanges[0];
    const base = new Date(`${newest}T00:00:00`).getTime();
    const cutoff = base - (rangeCfg.days - 1) * 24 * 60 * 60 * 1000;
    let list = allNews.filter((item) => {
      const matchTag = tag === '全部' || item.tag === tag;
      const matchDate = rangeCfg.days >= 9999 || new Date(`${item.date}T00:00:00`).getTime() >= cutoff;
      return matchTag && matchDate;
    });
    list = sort === 'latest' ? sortByLatest(list) : sortByHot(list);
    return list;
  }, [allNews, tag, range, sort, newest]);

  const visibleList: ContentItem[] = filtered.slice(0, visible);

  const reset = () => {
    setTag('全部');
    setRange('all');
    setSort('latest');
    setVisible(PAGE_SIZE);
  };

  const changeTag = (value: string) => {
    setTag(value);
    setVisible(PAGE_SIZE);
  };
  const changeRange = (value: string) => {
    setRange(value);
    setVisible(PAGE_SIZE);
  };

  return (
    <>
      <PageHeader
        title="最新新闻与热点资讯"
        description="聚焦社会、科技、财经领域的重大事件与深度解读，及时、客观、全面。"
        crumbs={[{ label: '首页', to: '/' }, { label: '资讯' }]}
        icon="ri-newspaper-line"
      />

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-8 lg:flex-row">
          {/* 筛选栏 */}
          <aside className="w-full lg:w-64 lg:flex-shrink-0">
            <div className="flex flex-col gap-6 rounded-lg border border-background-200/70 bg-background-50 p-5 lg:sticky lg:top-20">
              <div>
                <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-950">
                  <i className="ri-filter-3-line text-primary-600"></i>
                  新闻类型
                </h2>
                <div className="flex flex-wrap gap-2">
                  <Chip label="全部" active={tag === '全部'} onClick={() => changeTag('全部')} />
                  {tags.map((t) => (
                    <Chip key={t} label={t} active={tag === t} onClick={() => changeTag(t)} />
                  ))}
                </div>
              </div>

              <div>
                <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-950">
                  <i className="ri-calendar-line text-primary-600"></i>
                  发布时间
                </h2>
                <div className="flex flex-wrap gap-2">
                  {dateRanges.map((r) => (
                    <Chip key={r.key} label={r.label} active={range === r.key} onClick={() => changeRange(r.key)} />
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={reset}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          </aside>

          {/* 列表 */}
          <div className="min-w-0 flex-1">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                共找到 <strong className="text-foreground-950">{filtered.length}</strong> 条资讯
              </p>
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                <button
                  type="button"
                  onClick={() => setSort('latest')}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    sort === 'latest' ? 'bg-background-50 text-primary-600' : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className="ri-time-line mr-1"></i>最新
                </button>
                <button
                  type="button"
                  onClick={() => setSort('hot')}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    sort === 'hot' ? 'bg-background-50 text-primary-600' : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className="ri-fire-line mr-1"></i>最热
                </button>
              </div>
            </div>

            {visibleList.length === 0 ? (
              <EmptyState
                title="没有找到符合条件的资讯"
                description="试试放宽发布时间范围，或切换其他新闻类型，也许会有更多发现。"
                onReset={reset}
              />
            ) : (
              <>
                <div className="rounded-lg border border-background-200/70 bg-background-50 px-5 py-2 md:px-6">
                  {visibleList.map((item) => (
                    <ContentListItem key={item.id} item={item} />
                  ))}
                </div>
                {visible < filtered.length && (
                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-7 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      <i className="ri-add-line"></i>
                      加载更多（剩余 {filtered.length - visible} 条）
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}